/**
 * Sections shared by the English and Bengali case-study detail pages:
 * Article schema, hero, screenshot gallery, problem/strategy/evidence
 * breakdown and the related-cases grid. Wording comes from COPY per language.
 */
import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowUpRight, ExternalLink } from "lucide-react";
import { Section } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import type { CaseStudy } from "@/content/cases";
import { SITE } from "@/content/site";
import { CASE_STUDY_REVIEWED_ON, resolveCaseImage } from "@/content/case-study-media";

type Lang = "en" | "bn";

const COPY = {
  en: {
    back: "All case studies",
    backTo: "/case-studies",
    h1: "mt-6 font-display text-5xl md:text-7xl leading-[1.02]",
    problem: ["The problem", "What to review."],
    strategy: ["The strategy", "Suggested approach."],
    result: ["Evidence status", "What is supported."],
    duration: "Duration: ",
    more: "More case studies",
    viewAll: "View all",
    detailTo: "/case-studies/$slug",
    relatedMeta: "mt-6 text-xs uppercase tracking-[0.18em] text-muted-foreground",
    relatedTitle: "mt-2 font-display text-xl leading-tight",
  },
  bn: {
    back: "সব কেস স্টাডি",
    backTo: "/bn/case-studies",
    h1: "mt-6 font-display text-4xl md:text-6xl leading-[1.15]",
    problem: ["সমস্যা", "কী পরীক্ষা করবেন।"],
    strategy: ["কৌশল", "প্রস্তাবিত ধাপ।"],
    result: ["ফলাফল", "প্রমাণের অবস্থা।"],
    duration: "সময়কাল: ",
    more: "আরও কেস স্টাডি",
    viewAll: "সব দেখুন",
    detailTo: "/bn/case-studies/$slug",
    relatedMeta: "mt-6 text-xs tracking-[0.05em] text-muted-foreground",
    relatedTitle: "mt-2 font-display text-xl leading-snug",
  },
} as const;

/** schema.org Article for a case study page. */
export function caseStudyArticleSchema(c: CaseStudy, lang: Lang, enPath?: string) {
  const path = lang === "bn" ? `/bn/case-studies/${c.slug}` : `/case-studies/${c.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${SITE.url}${path}#article`,
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE.url}${path}` },
    headline: c.headline,
    description: c.summary,
    image: `${SITE.url}/og/case-${c.slug}.jpg`,
    author: {
      "@type": "Person",
      "@id": `${SITE.url}/#person`,
      name: SITE.fullName,
      url: `${SITE.url}${lang === "bn" ? "/bn/about" : "/about"}`,
      jobTitle: "SEO Expert & Digital Marketing Consultant",
      sameAs: Object.values(SITE.social),
    },
    reviewedBy: {
      "@type": "Person",
      "@id": `${SITE.url}/#person`,
      name: SITE.fullName,
    },
    publisher: {
      "@type": "Organization",
      name: SITE.name,
      url: SITE.url,
      logo: { "@type": "ImageObject", url: `${SITE.url}/og-image.jpg` },
    },
    datePublished: CASE_STUDY_REVIEWED_ON,
    dateModified: CASE_STUDY_REVIEWED_ON,
    inLanguage: lang,
    about: c.industry,
    ...(enPath ? { translationOfWork: { "@id": `${SITE.url}${enPath}#article` } } : {}),
  };
}

export function CaseHero({ c, lang }: { c: CaseStudy; lang: Lang }) {
  const t = COPY[lang];
  return (
    <article className="container-x pt-12 pb-24">
      <Link
        to={t.backTo}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft size={14} />
        {` ${t.back}`}
      </Link>

      <Reveal>
        <div className="mt-12 max-w-4xl">
          <div className="text-xs uppercase tracking-[0.2em] text-primary">
            {c.region} · {c.industry}
          </div>
          <h1 className={t.h1}>{c.headline}</h1>
          <p className="mt-8 text-xl text-muted-foreground max-w-3xl">{c.summary}</p>
        </div>
      </Reveal>
    </article>
  );
}

/** Screenshots and external proof links, when the case has any. */
export function CaseGallery({ c }: { c: CaseStudy }) {
  if (!c.images || c.images.length === 0) return null;
  return (
    <Section className="!pt-0">
      <div className="grid md:grid-cols-2 gap-6">
        {c.images.map((img) => (
          <div key={img.src} className="rounded-2xl overflow-hidden border border-border bg-card">
            <img
              src={resolveCaseImage(img.src)}
              alt={img.alt}
              loading="lazy"
              className="w-full h-auto block"
            />
          </div>
        ))}
      </div>
      {c.externalLinks && c.externalLinks.length > 0 && (
        <div className="mt-8 flex flex-wrap gap-3">
          {c.externalLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm hover:border-primary/40 hover:text-primary transition"
            >
              {l.label} <ExternalLink size={14} />
            </a>
          ))}
        </div>
      )}
    </Section>
  );
}

function ColumnHead({ label, title }: { label: string; title: string }) {
  return (
    <>
      <div className="text-xs uppercase tracking-[0.2em] text-primary mb-4">{label}</div>
      <h2 className="font-display text-3xl">{title}</h2>
    </>
  );
}

/** Problem / strategy / evidence columns. `footer` renders under the evidence column. */
export function CaseBreakdown({ c, lang, footer }: { c: CaseStudy; lang: Lang; footer: ReactNode }) {
  const t = COPY[lang];
  return (
    <Section>
      <div className="grid lg:grid-cols-3 gap-16">
        <div>
          <ColumnHead label={t.problem[0]} title={t.problem[1]} />
          <p className="mt-6 text-muted-foreground leading-relaxed">{c.problem}</p>
        </div>
        <div>
          <ColumnHead label={t.strategy[0]} title={t.strategy[1]} />
          <ol className="mt-6 space-y-4 text-muted-foreground leading-relaxed">
            {c.strategy.map((s, i) => (
              <li key={i} className="flex gap-4">
                <span className="font-display text-gradient text-xl flex-none">
                  {(i + 1).toString().padStart(2, "0")}
                </span>
                <span>{s}</span>
              </li>
            ))}
          </ol>
        </div>
        <div>
          <ColumnHead label={t.result[0]} title={t.result[1]} />
          <p className="mt-6 text-muted-foreground leading-relaxed">{c.result}</p>
          <div className="mt-8 text-sm text-muted-foreground">
            {t.duration}
            <span className="text-foreground">{c.duration}</span>
          </div>
          {footer}
        </div>
      </div>
    </Section>
  );
}

/** Up to three other cases in the same language. */
export function RelatedCases({ related, lang }: { related: readonly CaseStudy[]; lang: Lang }) {
  const t = COPY[lang];
  if (related.length === 0) return null;
  return (
    <Section className="!pt-0">
      <div className="flex items-end justify-between mb-10">
        <h2 className="font-display text-3xl md:text-4xl">{t.more}</h2>
        <Link
          to={t.backTo}
          className="text-sm text-primary hover:underline inline-flex items-center gap-1"
        >
          {`${t.viewAll} `}
          <ArrowUpRight size={14} />
        </Link>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {related.map((r) => (
          <Link
            key={r.slug}
            to={t.detailTo}
            params={{ slug: r.slug }}
            className="group block rounded-2xl border border-border bg-card p-8 hover:border-primary/40 hover:shadow-[var(--shadow-glow)] transition-all"
          >
            <div className="flex items-start justify-between">
              <span className="text-4xl">{r.cover}</span>
              <ArrowUpRight
                size={16}
                className="text-muted-foreground group-hover:text-primary transition"
              />
            </div>
            <div className={t.relatedMeta}>
              {r.region} · {r.industry}
            </div>
            <h3 className={t.relatedTitle}>{r.headline}</h3>
          </Link>
        ))}
      </div>
    </Section>
  );
}
